/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { TrackingDataV2 } from '@oneaudi/audi-tracking-service';

export const appReady: TrackingDataV2 = {
  event: {
    eventInfo: {
      eventAction: 'feature_app_ready',
      eventName: 'one header - feature app ready',
    },
    attributes: {
      componentName: 'one-header',
      label: '',
      targetURL: '',
      clickID: '',
      elementName: '',
      value: '',
      pos: '',
    },
  },
};

export enum EventAction {
  INTERNAL = 'internal_link',
  EXIT = 'exit_link',
  COMPONENT_NAME = 'one-header',
  MAIN_NAVIGATION = 'main-navigation',
  NAVIGATION = 'navigation',
  NONE_INTERACTION = 'non_interaction',
}

const getIndexPosition = (index: number): number => {
  const indexPosition = index + 1;
  return indexPosition;
};

const isAbsoluteURL = (url: string): boolean => {
  const r = /^(?:[a-z+]+:)?\/\//i;
  return r.test(url);
};

export function getLinkType(originUrl: string, targetUrl: string): EventAction {
  if (isAbsoluteURL(targetUrl) && isAbsoluteURL(originUrl)) {
    if (new URL(originUrl).host !== new URL(targetUrl).host) {
      return EventAction.EXIT;
    }
  }

  return EventAction.INTERNAL;
}

export function createBaseTrackingData(eventName: string, componentName: string): TrackingDataV2 {
  return {
    event: {
      eventInfo: {
        eventAction: 'navigation',
        eventName,
      },
      attributes: {
        componentName,
        label: '',
        targetURL: '',
        clickID: '',
        elementName: '',
        pos: '',
        value: '',
      },
    },
  };
}

export function impressionEvent(title: string): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    `one header - ${title}`,
    EventAction.COMPONENT_NAME,
  );

  if (trackingStructure.event.eventInfo) {
    trackingStructure.event.eventInfo.eventAction = 'non_interaction';
  }

  if (trackingStructure.event.attributes) {
    trackingStructure.event.attributes.elementName = 'one-header';
    trackingStructure.event.attributes.label = '';
    trackingStructure.event.attributes.clickID = '';
    trackingStructure.event.attributes.pos = '';
    trackingStructure.event.attributes.value = '';
  }
  return trackingStructure;
}

export function myAudiPopoverClick(title: string, positionIndex: number): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    `one header - ${title}`,
    EventAction.COMPONENT_NAME,
  );
  if (trackingStructure.event.attributes) {
    trackingStructure.event.attributes.elementName = 'text link';
    trackingStructure.event.attributes.label = 'myAudi';
    trackingStructure.event.attributes.clickID = '';
    trackingStructure.event.attributes.pos = positionIndex.toString();
  }

  return trackingStructure;
}

export function mobileMenuClick(
  title: string,
  positionIndex: number,
  elementName: string,
): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    `one header - ${title}`,
    EventAction.COMPONENT_NAME,
  );
  if (trackingStructure.event.attributes) {
    trackingStructure.event.attributes.elementName = elementName;
    trackingStructure.event.attributes.label = 'Menu';
    trackingStructure.event.attributes.clickID = '';
    trackingStructure.event.attributes.pos = getIndexPosition(positionIndex).toString();
  }

  return trackingStructure;
}

export function logoTrackingData(dynamicTargetURL?: string): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    `one header - click on audi logo`,
    EventAction.COMPONENT_NAME,
  );
  const eventAction = getLinkType(window.location.href, dynamicTargetURL || '');
  if (trackingStructure.event.attributes) {
    trackingStructure.event.attributes.targetURL = dynamicTargetURL;
    trackingStructure.event.attributes.elementName = 'icon';
    trackingStructure.event.attributes.label = 'Audi Logo';
    trackingStructure.event.attributes.clickID = '';
  }
  if (trackingStructure.event.eventInfo)
    trackingStructure.event.eventInfo.eventAction = eventAction;

  return trackingStructure;
}

export function logoutClickOnWishlist(positionIndex: number): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    'one header - click on heart (user not logged in)',
    EventAction.COMPONENT_NAME,
  );
  if (trackingStructure.event.attributes) {
    trackingStructure.event.attributes.elementName = 'icon';
    trackingStructure.event.attributes.label = 'Heart';
    trackingStructure.event.attributes.pos = getIndexPosition(positionIndex).toString();
  }
  return trackingStructure;
}

export function loginClickOnWishlist(
  dynamicTargetURL: string,
  positionIndex: number,
): TrackingDataV2 {
  const eventAction = getLinkType(window.location.href, dynamicTargetURL);
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    'one header - click on heart',
    EventAction.COMPONENT_NAME,
  );

  if (trackingStructure.event.attributes) {
    trackingStructure.event.attributes.targetURL = dynamicTargetURL;
    trackingStructure.event.attributes.elementName = 'icon';
    trackingStructure.event.attributes.label = 'Heart';
    trackingStructure.event.attributes.pos = getIndexPosition(positionIndex).toString();
  }
  if (trackingStructure.event.eventInfo)
    trackingStructure.event.eventInfo.eventAction = eventAction;

  return trackingStructure;
}

export function mainNavigationToggle(
  title: string,
  element: Element | null,
  positionIndex: number,
): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    `one header - ${title}`,
    EventAction.COMPONENT_NAME,
  );

  if (trackingStructure.event.attributes && element) {
    const linkLabel = element.getElementsByTagName('span');
    trackingStructure.event.attributes.componentName = EventAction.COMPONENT_NAME;
    trackingStructure.event.attributes.elementName = 'text link';
    trackingStructure.event.attributes.label = `Menu > ${linkLabel?.[0]?.innerText || ''}`;
    trackingStructure.event.attributes.clickID = '';
    trackingStructure.event.attributes.targetURL = element.getAttribute('href') || '';
    trackingStructure.event.attributes.pos = getIndexPosition(positionIndex).toString();
  }
  if (trackingStructure.event.eventInfo)
    trackingStructure.event.eventInfo.eventAction = EventAction.NAVIGATION;

  return trackingStructure;
}

export function mainNavigationLinkClick(
  title: string,
  element: Element | null,
  positionIndex: number,
): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    `one header - ${title}`,
    EventAction.COMPONENT_NAME,
  );
  const url = element?.getAttribute('href') || '';
  const eventAction = getLinkType(window.location.href, url || '');
  if (trackingStructure.event.attributes) {
    trackingStructure.event.attributes.elementName = 'text link';
    // @ts-expect-error
    trackingStructure.event.attributes.label = element?.innerText;
    trackingStructure.event.attributes.clickID = '';
    trackingStructure.event.attributes.pos = positionIndex.toString();
    trackingStructure.event.attributes.targetURL = url;
  }
  if (trackingStructure.event.eventInfo)
    trackingStructure.event.eventInfo.eventAction = eventAction;

  return trackingStructure;
}

export function subNavigationLinkClick(
  title: string,
  element: Element | null,
  positionIndex: number,
): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    `one header - ${title}`,
    EventAction.MAIN_NAVIGATION,
  );
  const url = element?.getAttribute('href') || '';
  const eventAction = getLinkType(window.location.href, url || '');
  if (trackingStructure.event.attributes && element) {
    const parentElement = element?.closest('div[role="tabpanel"]');
    const parentLink = parentElement?.previousElementSibling;
    const parentLabel = parentLink?.getElementsByTagName('span');
    // @ts-ignore
    const elementIndex = !Number(getIndexPosition(element ? element?.dataset.index : undefined))
      ? '' // @ts-ignore
      : `-${Number(element?.dataset?.index)}`;
    // @ts-ignore
    const linkLabel = element?.innerText;
    const label = `Menu${parentLabel?.[0]?.innerText ? ` > ${parentLabel?.[0]?.innerText}` : ''} ${linkLabel ? `> ${linkLabel}` : ''}`;

    trackingStructure.event.attributes.componentName = EventAction.COMPONENT_NAME;
    trackingStructure.event.attributes.elementName = 'text link';
    trackingStructure.event.attributes.label = label;
    trackingStructure.event.attributes.clickID = '';
    trackingStructure.event.attributes.targetURL = url;
    trackingStructure.event.attributes.pos = `${getIndexPosition(positionIndex)}${elementIndex}`;
  }
  if (trackingStructure.event.eventInfo)
    trackingStructure.event.eventInfo.eventAction = eventAction;

  return trackingStructure;
}

export function onDealerTrackingData(element: Element | null, label: string): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    `one header - click on selected dealer`,
    EventAction.COMPONENT_NAME,
  );
  let dynamicTargetURL = '';
  if (trackingStructure.event.attributes && element) {
    dynamicTargetURL = trackingStructure.event.attributes.targetURL =
      element?.getAttribute('href') || '';
    trackingStructure.event.attributes.targetURL = dynamicTargetURL;
    trackingStructure.event.attributes.elementName = 'button';
    trackingStructure.event.attributes.label = label;
    trackingStructure.event.attributes.clickID = '';
  }
  if (trackingStructure.event.eventInfo && dynamicTargetURL) {
    const eventAction = getLinkType(window.location.href, dynamicTargetURL || '');
    trackingStructure.event.eventInfo.eventAction = eventAction;
  }

  return trackingStructure;
}

export function removeDealerTrackingData(label: string): TrackingDataV2 {
  const trackingStructure: TrackingDataV2 = createBaseTrackingData(
    'one header - click to disconnect dealer',
    EventAction.COMPONENT_NAME,
  );
  if (trackingStructure.event.attributes) {
    trackingStructure.event.attributes.elementName = 'text link';
    trackingStructure.event.attributes.label = label;
    trackingStructure.event.attributes.pos = '';
  }
  return trackingStructure;
}
